// src/extensions/MergeTag.ts
import { TextSelection } from "@tiptap/pm/state";
import { Node } from "@tiptap/core";
import { mergeTagInputRule } from "./mergeTagInputRule";
import { mergeTagScannerPlugin } from "./mergeTagScannerPlugin";
import getMTRegex from "./getMTRegex";
import { Emitter } from "mitt";
import { IMergeTag } from "../../../types/MergeTags";

// flatMergeTags/showTagText accept either a static value or a getter. Passing a
// getter lets the node view resolve display names against the *current* catalog
// instead of a snapshot captured at configure() time (the catalog can change
// mid-session via setMergeTags), without re-creating the editor.
type MaybeGetter<T> = T | (() => T);

export interface MergeTagOptions {
  patterns?: Record<string, { regex: string; label: string }>;
  flatMergeTags?: MaybeGetter<IMergeTag[]>;
  showTagText?: MaybeGetter<boolean>;
  syntax: {
    start: string;
    end: string;
  };
  emitter?: any;
}

const resolve = <T>(value: MaybeGetter<T> | undefined): T | undefined =>
  typeof value === "function" ? (value as () => T)() : value;

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    mergeTag: {
      insertMergeTag: (tag: string) => ReturnType;
    };
  }
}

export const MergeTag = Node.create<MergeTagOptions>({
  name: "mergeTag",
  group: "inline",
  inline: true,
  atom: true,
  marks: "_",

  addAttributes() {
    return {
      tag: {
        default: "",
      },
    };
  },

  parseHTML() {
    const { start, end } = this.options.syntax;

    const getAttrs = (element: HTMLElement) => {
      const text = (element.textContent || "").trim();
      const data = (element.getAttribute("data-merge-tag") || "").trim();

      if (text.includes(start)) return { tag: text };
      if (data.includes(start)) return { tag: data };
      if (data) return { tag: `${start}${data}${end}` };
      return { tag: text };
    };

    // span[data-merge-tag] is the current serialization format (see renderHTML).
    // button[data-merge-tag] is kept for backward compatibility with older templates.
    // Legacy templates containing raw merge-tag text (e.g. "*|FNAME|*" without a
    // wrapper element) are handled by mergeTagScannerPlugin, which converts them
    // into proper nodes on the first transaction after setContent.
    return [
      { tag: "span[data-merge-tag]", getAttrs },
      { tag: "button[data-merge-tag]", getAttrs },
    ];
  },

  renderHTML({ node }) {
    const { start, end } = this.options.syntax;

    // Keep the full syntax in the visible text, but expose only the clean tag
    // name in the data attribute so consumers don't have to strip the syntax.
    const cleanTag = stripSyntax(node.attrs.tag, start, end);

    return ["span", { "data-merge-tag": cleanTag }, node.attrs.tag];
  },

  addCommands() {
    return {
      insertMergeTag:
        (tag: string) =>
        ({ state, dispatch }) => {
          const { tr, schema } = state;
          const nodeType = schema.nodes[this.name];
          const marks = state.storedMarks ?? state.selection.$from.marks();
          const node = nodeType.create({ tag }, null, marks);
          if (dispatch) {
            tr.replaceSelectionWith(node);
            dispatch(tr.scrollIntoView());
          }
          return true;
        },
    };
  },

  addNodeView() {
    const syntax = this.options.syntax;

    // Patterns come from static config — compile their regexes once instead of
    // recompiling on every node render.
    const compiledPatterns = Object.values(this.options.patterns ?? {}).map((pattern) => ({
      label: pattern.label,
      regex: new RegExp(pattern.regex),
    }));

    // Memoize the value->tag lookup against the catalog array's identity so we
    // build the Map once per catalog, not once per node, yet still pick up a
    // replaced catalog (setMergeTags hands back a fresh array reference).
    let cachedSource: IMergeTag[] | null = null;
    let cachedMap = new Map<string, IMergeTag>();
    const lookup = (value: string): IMergeTag | undefined => {
      const source = resolve(this.options.flatMergeTags) ?? [];
      if (source !== cachedSource) {
        cachedSource = source;
        cachedMap = new Map(source.map((t) => [t.value, t]));
      }
      return cachedMap.get(value);
    };

    const resolveDisplayText = (tag: string): string => {
      // catalog (opt-in) -> pattern -> raw value
      if (resolve(this.options.showTagText)) {
        const known = lookup(tag);
        if (known?.text) {
          return known.text;
        }
      }

      const tagLabel = stripSyntax(tag, syntax.start, syntax.end);
      for (const { label, regex } of compiledPatterns) {
        const match = regex.exec(tagLabel);
        if (match) {
          const full = match[0];
          const left = match[1] || "";
          const right = match[2] || "";
          const middle = full.slice(left.length, full.length - right.length);
          const after = tagLabel.slice(full.length);
          return label + " " + middle + " " + after;
        }
      }

      return tag;
    };

    return ({ node, view, getPos }) => {
      const dom = document.createElement("button");

      dom.innerText = resolveDisplayText(node.attrs.tag);
      dom.className = "merge-tag topol-special-merge-tag";
      dom.contentEditable = "false";

      // možnost pro budoucí klikací dialog
      dom.addEventListener("click", async (e) => {
        e.preventDefault();
        const newTag = await selectNewMT(node.attrs.tag, this.options.emitter);
        const { state, dispatch } = view;
        const pos = getPos() || 0;
        let posAfter = pos + node.nodeSize;
        if (newTag) {
          const nodeType = state.schema.nodes.mergeTag;
          const newNode = nodeType.create({ tag: newTag }, null, node.marks);

          dispatch(state.tr.replaceWith(pos, pos + node.nodeSize, newNode));
          posAfter = pos + newNode.nodeSize;
        }
        const { state: finalState, dispatch: finalDispatch } = view;
        const trAfter = finalState.tr.setSelection(
          TextSelection.near(finalState.doc.resolve(posAfter)),
        );
        finalDispatch(trAfter);
        view.focus();
      });

      return { dom };
    };
  },

  addInputRules() {
    const starInputRegex = new RegExp(getMTRegex(this.options.syntax) + "$");
    return [
      mergeTagInputRule({
        find: starInputRegex,
        type: this.type,
        getAttributes: (match) => ({ tag: match[0] }),
      }),
    ];
  },

  addProseMirrorPlugins() {
    return [mergeTagScannerPlugin(this.options.syntax)];
  },
});

function stripSyntax(tag: string, start: string, end: string): string {
  return tag.startsWith(start) && tag.endsWith(end)
    ? tag.slice(start.length, tag.length - end.length)
    : tag;
}

async function selectNewMT(oldTag: string, emitter: any): Promise<string> {
  return new Promise((resolve) => {
    emitter.emit("selectNewMT", {
      oldTag,
      resolve,
    });
  });
}
